import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { Card, CardBody, CardHeader, CardTitle, InputGroup, Button, H2 } from '@/common'
import { Routes, Route } from 'react-router-dom'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useUpdateUserName } from '@/features/profile/hooks/user'
import toast from 'react-hot-toast'
import { Container } from '@/shared/components'

export default function ProfilePage() {
  return (
    <Routes>
      <Route path="/" element={<Profile />} />
    </Routes>
  )
}

function Profile() {
  const { session } = useSession()
  const { t } = useI18n()
  const [firstName, setFirstName] = useState(session.firstName)
  const [lastName, setLastName] = useState(session.lastName)

  useEffect(() => {
    setFirstName(session.firstName)
    setLastName(session.lastName)
  }, [session.firstName, session.lastName])

  const isFormPristine = useMemo(() => {
    return firstName === session.firstName && lastName === session.lastName
  }, [firstName, lastName, session.firstName, session.lastName])

  const isSaveDisabled = useMemo(() => {
    const isFirstNameValid = firstName.trim().length > 0
    const isLastNameValid = lastName.trim().length > 0
    return isFormPristine || !isFirstNameValid || !isLastNameValid
  }, [firstName, lastName, isFormPristine])

  const { mutateAsync: updateUserName, isPending: isSaving } = useUpdateUserName()

  const onSave = useCallback(async () => {
    try {
      await updateUserName({ firstName: firstName.trim(), lastName: lastName.trim() })
      toast.success(t('profile:notifications.saveChanges'))
    } catch (error) {
      console.error(error)
      toast.error(t('profile:notifications.saveChangesError'))
    }
  }, [updateUserName, firstName, lastName, t])

  return (
    <div>
      <Container>
        <header>
          <H2>{t('profile:title')}</H2>
        </header>
        <section>
          <Card>
            <CardHeader className="flex justify-between items-center">
              <CardTitle title={t('profile:profileDetails.title')} />
              <Button onClick={onSave} disabled={isSaveDisabled} isLoading={isSaving} sizeType="sm">
                {t('common:actions.save')}
              </Button>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <InputGroup
                    label={t('profile:profileDetails.firstName')}
                    value={firstName}
                    onChange={evt => setFirstName((evt.target as HTMLInputElement).value)}
                  />
                  <InputGroup
                    label={t('profile:profileDetails.lastName')}
                    value={lastName}
                    onChange={evt => setLastName((evt.target as HTMLInputElement).value)}
                  />
                </div>
                <InputGroup
                  disabled
                  label={t('profile:profileDetails.email')}
                  value={session.email}
                />
              </div>
            </CardBody>
          </Card>
        </section>
      </Container>
    </div>
  )
}
